// Live DNS status indicator for a workspace's customDomain.
//
// Shared by the workspace-side white-label settings card (Task #139) and
// the platform-admin workspace list (Task #155) so both surfaces show the
// exact same five-state diagnosis with identical copy.
import { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";

export type DomainStatus =
  | "no_domain"
  | "not_propagated"
  | "resolves_elsewhere"
  | "resolves_to_us_unassociated"
  | "resolves_to_us_match";

export interface DomainStatusReport {
  domain: string | null;
  cnameTarget: string;
  status: DomainStatus;
  hint: string;
  resolvedCnames: string[];
  resolvedAddresses: string[];
  ourAddresses: string[];
  isAssociatedWithWorkspace: boolean;
  associatedWithOtherWorkspace: boolean;
  verifiedAt: string | null;
  checkedAt: string;
}

interface DomainStatusPanelProps {
  queryUrl: string;
  queryKey: unknown[];
  // Optional verify endpoint — when present the panel auto-fires it once
  // the GET reports `resolves_to_us_match` but the workspace isn't yet
  // verified, replacing the GET-side-effect that used to live on the read
  // endpoint. Defaults to `/api/admin/domain-status/verify` for the
  // workspace-admin surface; the platform-admin surface (which renders
  // this panel for read-only inspection of arbitrary workspaces) leaves
  // it unset so it never auto-verifies on someone else's behalf.
  verifyUrl?: string | null;
}

export function DomainStatusPanel({
  queryUrl,
  queryKey,
  verifyUrl = "/api/admin/domain-status/verify",
}: DomainStatusPanelProps) {
  const { data, isLoading, isFetching, refetch, error } = useQuery<DomainStatusReport>({
    queryKey,
    queryFn: async () => {
      const r = await apiRequest("GET", queryUrl);
      return r.json();
    },
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  });

  // POST-based verify side effect — replaces the GET auto-verify that
  // used to fire from the read endpoint. Triggered automatically once
  // the read reports a healthy match that hasn't been verified yet, and
  // also wired to manual "Re-check" so the admin can force a retry.
  const verify = useMutation({
    mutationFn: async () => {
      if (!verifyUrl) return null;
      // Forward the same `?domain=` query the read used so the verify
      // operates on the same hostname the admin saw "OK" for.
      const sep = verifyUrl.includes("?") ? "&" : "?";
      const qIdx = queryUrl.indexOf("?");
      const url = qIdx >= 0 ? `${verifyUrl}${sep}${queryUrl.slice(qIdx + 1)}` : verifyUrl;
      const r = await apiRequest("POST", url);
      return r.json() as Promise<DomainStatusReport>;
    },
    onSuccess: (next) => {
      if (next) {
        queryClient.setQueryData(queryKey, next);
      }
    },
  });

  // Auto-verify guard: only fire once per (domain, status) combo so we
  // don't loop if the verify call doesn't actually flip verifiedAt. We
  // run this in a useEffect (not during render) so React strict-mode's
  // double-invocation of render functions can't double-POST before the
  // mutation's `isPending` flag has flipped.
  const shouldAutoVerify =
    !!verifyUrl &&
    !!data &&
    data.status === "resolves_to_us_match" &&
    data.isAssociatedWithWorkspace &&
    !data.verifiedAt &&
    !verify.isPending &&
    !verify.isSuccess &&
    !verify.isError;
  useEffect(() => {
    if (shouldAutoVerify) verify.mutate();
    // verify is a stable mutation object across renders; tracking the
    // boolean is enough to gate the single auto-fire per (domain,status).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoVerify]);

  if (isLoading) {
    return (
      <div
        className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground flex items-center gap-2"
        data-testid="status-domain-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking DNS…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start justify-between gap-3"
        data-testid="status-domain-error"
      >
        <span>Couldn't check DNS status right now.</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2"
          onClick={() => refetch()}
          data-testid="button-domain-status-retry"
        >
          <RefreshCw className="h-3 w-3 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const meta: Record<DomainStatus, {
    label: string;
    icon: JSX.Element;
    tone: string;
    border: string;
    bg: string;
  }> = {
    no_domain: {
      label: "No domain configured",
      icon: <Globe className="h-3.5 w-3.5" />,
      tone: "text-muted-foreground",
      border: "border-border",
      bg: "bg-muted/40",
    },
    not_propagated: {
      label: "DNS not yet propagated",
      icon: <AlertCircle className="h-3.5 w-3.5" />,
      tone: "text-amber-700 dark:text-amber-400",
      border: "border-amber-200 dark:border-amber-900/60",
      bg: "bg-amber-50 dark:bg-amber-950/30",
    },
    resolves_elsewhere: {
      label: "DNS resolves elsewhere",
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
      tone: "text-red-700 dark:text-red-400",
      border: "border-red-200 dark:border-red-900/60",
      bg: "bg-red-50 dark:bg-red-950/30",
    },
    resolves_to_us_unassociated: {
      label: "Points to us, not yet linked to this workspace",
      icon: <AlertCircle className="h-3.5 w-3.5" />,
      tone: "text-amber-700 dark:text-amber-400",
      border: "border-amber-200 dark:border-amber-900/60",
      bg: "bg-amber-50 dark:bg-amber-950/30",
    },
    resolves_to_us_match: {
      label: "DNS resolves to us — looks good",
      icon: <CheckCircle className="h-3.5 w-3.5" />,
      tone: "text-green-700 dark:text-green-400",
      border: "border-green-200 dark:border-green-900/60",
      bg: "bg-green-50 dark:bg-green-950/30",
    },
  };
  const m = meta[data.status];
  const detailLine =
    data.resolvedCnames.length > 0
      ? `CNAME → ${data.resolvedCnames.join(", ")}`
      : data.resolvedAddresses.length > 0
        ? `A/AAAA → ${data.resolvedAddresses.slice(0, 3).join(", ")}${data.resolvedAddresses.length > 3 ? "…" : ""}`
        : null;

  const onRecheck = async () => {
    // Re-run the read first, then optionally re-verify.
    const r = await refetch();
    const fresh = r.data;
    if (
      verifyUrl &&
      fresh &&
      fresh.status === "resolves_to_us_match" &&
      fresh.isAssociatedWithWorkspace &&
      !fresh.verifiedAt
    ) {
      verify.reset();
      verify.mutate();
    }
  };

  return (
    <div
      className={`rounded-md border p-3 ${m.border} ${m.bg}`}
      data-testid="status-domain"
      data-status={data.status}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <span className={`mt-0.5 ${m.tone}`}>{m.icon}</span>
          <div className="min-w-0 flex-1">
            <div className={`text-xs font-medium ${m.tone}`} data-testid="text-domain-status-label">
              {m.label}
            </div>
            <p className="text-xs text-foreground/80 mt-0.5 leading-relaxed" data-testid="text-domain-status-hint">
              {data.hint}
            </p>
            {detailLine && (
              <p className="text-[11px] font-mono text-muted-foreground mt-1 break-all" data-testid="text-domain-status-detail">
                {detailLine}
              </p>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 shrink-0"
          onClick={onRecheck}
          disabled={isFetching || verify.isPending}
          data-testid="button-domain-status-refresh"
          title="Re-check DNS"
        >
          <RefreshCw className={`h-3 w-3 ${(isFetching || verify.isPending) ? "animate-spin" : ""}`} />
          <span className="ml-1 text-[11px]">Re-check</span>
        </Button>
      </div>
    </div>
  );
}
